package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.MobileDashboardBackHandler
import org.example.project.service.NotepadService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckboxSymbol
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement

@Composable
fun MobileNotepadLayout(art: String) {
    Style(NotepadStyles)
    val scope = rememberCoroutineScope()
    val notes = NotepadService.getNotes(art)
    var selectedId by remember(art) { mutableStateOf<String?>(null) }
    fun text(key: String, fallback: String): String =
        LocalizationManager.get(key).takeUnless { it == "[$key]" } ?: fallback

    LaunchedEffect(art) {
        NotepadService.setNotes(art, loadNotesForArt(art))
    }

    if (selectedId == null) {
        MobileNotepadGrid(
            notes = notes,
            emptyNoteLabel = text("driver_notes_note", "Notiz"),
            onCreate = { noteType ->
                scope.launch {
                    val draft = All(
                        id = "neu",
                        titel = text("driver_notes_new", "Neue Notiz"),
                        beschreibung = "",
                        farbe = "linear-gradient(145deg, rgba(34,39,50,0.98), rgba(17,20,27,0.98))",
                        art = noteType
                    )
                    val saved = saveNoteForArt(art, draft) ?: return@launch
                    NotepadService.setNotes(art, NotepadService.getNotes(art) + saved)
                    selectedId = saved.id
                }
            },
            onOpen = { selectedId = it },
            onDelete = { noteId ->
                scope.launch {
                    deleteNoteForArt(art, noteId)
                    NotepadService.removeNote(art, noteId)
                }
            }
        )
    } else {
        val selected = notes.firstOrNull { it.id == selectedId }
        if (selected == null) {
            selectedId = null
        } else {
            MobileNotepadEditor(
                note = selected,
                backLabel = text("driver_notes_back", "Zurück"),
                onSave = { updated ->
                    NotepadService.updateNote(art, updated)
                    scope.launch {
                        saveNoteForArt(art, updated)?.let { NotepadService.updateNote(art, it) }
                    }
                },
                onDelete = {
                    scope.launch {
                        deleteNoteForArt(art, selected.id)
                        NotepadService.removeNote(art, selected.id)
                        selectedId = null
                    }
                },
                onClose = { selectedId = null }
            )
        }
    }
}

@Composable
private fun MobileNotepadGrid(
    notes: List<All>,
    emptyNoteLabel: String,
    onCreate: (String) -> Unit,
    onOpen: (String) -> Unit,
    onDelete: (String) -> Unit
) {
    val tabletLayout = window.innerWidth >= 700
    val cardHeight = if (tabletLayout) 420.px else 520.px
    Div({
        style {
            width(100.percent)
            height(100.percent)
            overflowY("auto")
            padding(14.px)
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns(if (tabletLayout) "repeat(2, minmax(0, 1fr))" else "minmax(0, 1fr)")
                gap(12.px)
                padding(0.px, 0.px, 20.px, 0.px)
            }
        }) {
            Div({
                style {
                    height(cardHeight)
                    borderRadius(14.px)
                    property("border", "2px dashed rgba(255,255,255,0.24)")
                    backgroundColor(Color("rgba(255,255,255,0.05)"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Stretch)
                    justifyContent(JustifyContent.SpaceBetween)
                    flexDirection(FlexDirection.Column)
                    color(Color.white)
                    fontSize(42.px)
                }
            }) {
                Div({ style { property("flex", "1"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center) } }) { Text("+") }
                Div({ style { display(DisplayStyle.Grid); gridTemplateColumns("1fr 1fr"); property("border-top", "1px solid rgba(255,255,255,.16)") } }) {
                    Div({
                        attr("title", "Paintnotiz erstellen")
                        style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); padding(10.px); cursor("pointer"); property("border-right", "1px solid rgba(255,255,255,.16)") }
                        onClick { onCreate("PAINT") }
                    }) { Img(src = "images/stift.svg", attrs = { style { width(24.px); height(24.px) } }) }
                    Div({
                        attr("title", "Textnotiz erstellen")
                        style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); padding(10.px); cursor("pointer") }
                        onClick { onCreate("TEXT") }
                    }) { Img(src = "images/format_color_text.svg", attrs = { style { width(24.px); height(24.px) } }) }
                }
            }

            notes.forEach { note ->
                val paintPreview = note.art.equals("PAINT", ignoreCase = true) &&
                    note.beschreibung.orEmpty().startsWith("data:image/")
                Div({
                    style {
                        position(Position.Relative)
                        height(cardHeight)
                        borderRadius(14.px)
                        property("background", normalizedNoteBackground(note.farbe))
                        padding(14.px)
                        property("box-sizing", "border-box")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                        cursor("pointer")
                        overflow("hidden")
                    }
                    onClick { onOpen(note.id) }
                }) {
                    Img(src = "images/miniclose.svg", attrs = {
                        attr("title", "Notiz löschen")
                        style {
                            position(Position.Absolute)
                            top(10.px)
                            right(10.px)
                            width(16.px)
                            height(16.px)
                            cursor("pointer")
                            property("z-index", "2")
                            property("filter", "brightness(0) invert(1)")
                        }
                        onClick {
                            it.stopPropagation()
                            onDelete(note.id)
                        }
                    })
                    Span({
                        style {
                            paddingRight(24.px)
                            fontSize(16.px)
                            fontWeight("700")
                            color(Color.white)
                            property("overflow-wrap", "anywhere")
                        }
                    }) { Text(note.titel.orEmpty().ifBlank { emptyNoteLabel }) }
                    if (paintPreview) {
                        MobilePaintPreview(
                            note.imageValues["paintPreview"].orEmpty().ifBlank { note.beschreibung.orEmpty() },
                            note.titel.orEmpty(),
                            note.imageValues["paintViewportWidth"]?.toDoubleOrNull(),
                            note.imageValues["paintViewportHeight"]?.toDoubleOrNull()
                        )
                    } else {
                        Div({
                            ref { element ->
                                element.innerHTML = normalizeStoredNoteHtml(note.beschreibung)
                                onDispose { }
                            }
                            style {
                                property("flex", "1")
                                minHeight(0.px)
                                fontSize(15.px)
                                color(Color.white)
                                opacity(0.82)
                                overflow("hidden")
                                property("line-height", "1.45")
                                property("overflow-wrap", "anywhere")
                                property("pointer-events", "none")
                                property("user-select", "none")
                            }
                        })
                    }
                }
            }
        }
    }
}

@Composable
private fun MobilePaintPreview(source: String, title: String, viewportWidth: Double?, viewportHeight: Double?) {
    Div({
        style {
            width(100.percent)
            property("flex", "1")
            minHeight(0.px)
            overflow("hidden")
            property("pointer-events", "none")
            property("user-select", "none")
        }
    }) {
        Img(src = source, attrs = {
            attr("alt", title)
            style {
                if (viewportWidth != null && viewportWidth > 0.0) width(viewportWidth.px) else width(100.percent)
                if (viewportHeight != null && viewportHeight > 0.0) height(viewportHeight.px) else height(100.percent)
                property("max-width", "none")
                property("max-height", "none")
                property("object-fit", "fill")
                display(DisplayStyle.Block)
                backgroundColor(Color.transparent)
            }
        })
    }
}

@Composable
private fun MobileNotepadEditor(
    note: All,
    backLabel: String,
    onSave: (All) -> Unit,
    onDelete: () -> Unit,
    onClose: () -> Unit
) {
    val isPaint = note.art.equals("PAINT", ignoreCase = true)
    var title by remember(note.id) { mutableStateOf(note.titel.orEmpty()) }
    var html by remember(note.id) { mutableStateOf(normalizeStoredNoteHtml(note.beschreibung)) }
    var drawingData by remember(note.id) { mutableStateOf(note.beschreibung.orEmpty().takeIf { it.startsWith("data:image/") }.orEmpty()) }
    var editor by remember(note.id) { mutableStateOf<HTMLElement?>(null) }
    var savedRange by remember(note.id) { mutableStateOf<dynamic>(null) }
    var paintInteraction by remember(note.id) { mutableStateOf("DRAW") }
    var noteColor by remember(note.id) { mutableStateOf("#ffffff") }
    var showColorPalette by remember(note.id) { mutableStateOf(false) }
    var activeInsertMode by remember(note.id) { mutableStateOf<String?>(null) }
    var deleting by remember(note.id) { mutableStateOf(false) }

    fun currentNote(): All {
        if (isPaint) {
            val canvasId = "mobile-note-drawing-${note.id}"
            val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
            if (canvas != null) drawingData = canvas.toDataURL("image/png")
            val preview = noteDrawingViewportDataUrl(canvasId)
            val liveCanvas = canvas
            val viewport = document.getElementById("$canvasId-viewport") as? HTMLElement
            val nextImageValues = note.imageValues.toMutableMap().apply {
                if (!preview.isNullOrBlank()) put("paintPreview", preview)
                put("paintPanX", liveCanvas?.getAttribute("data-pan-x").orEmpty().ifBlank { "0" })
                put("paintPanY", liveCanvas?.getAttribute("data-pan-y").orEmpty().ifBlank { "0" })
                put("paintViewportWidth", viewport?.clientWidth?.toString().orEmpty().ifBlank { "0" })
                put("paintViewportHeight", viewport?.clientHeight?.toString().orEmpty().ifBlank { "0" })
            }
            return note.copy(titel = title, beschreibung = drawingData, art = "PAINT", imageValues = nextImageValues)
        } else {
            html = editor?.innerHTML ?: html
        }
        return note.copy(titel = title, beschreibung = html, art = "TEXT")
    }

    fun saveAndClose() {
        onSave(currentNote())
        onClose()
    }

    fun insertToken(tokenHtml: String) {
        restoreEditorSelection(editor, savedRange)
        insertHtmlAtSelection(editor, "$tokenHtml&nbsp;")
        html = editor?.innerHTML.orEmpty()
        savedRange = captureEditorSelection(editor)
        onSave(currentNote())
        editor?.focus()
    }

    DisposableEffect(note.id) {
        val backHandler: () -> Boolean = {
            saveAndClose()
            true
        }
        MobileDashboardBackHandler.handler.value = backHandler
        onDispose {
            if (MobileDashboardBackHandler.handler.value === backHandler) {
                MobileDashboardBackHandler.handler.value = null
            }
            if (!deleting) onSave(currentNote())
        }
    }

    Div({
        style {
            position(Position.Relative)
            width(100.percent)
            height(100.percent)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("background", normalizedNoteBackground(note.farbe))
            overflow("hidden")
        }
    }) {
        Div({
            style {
                position(Position.Relative)
                property("z-index", "30")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
                padding(12.px)
                property("border-bottom", "1px solid rgba(255,255,255,0.12)")
            }
        }) {
            Input(InputType.Text, attrs = {
                value(title)
                style {
                    property("flex", "1")
                    minWidth(0.px)
                    border(0.px)
                    backgroundColor(Color.transparent)
                    outline("none")
                    color(Color.white)
                    fontSize(19.px)
                    fontWeight("700")
                    textAlign("center")
                }
                onInput { title = it.value }
            })
        }
        if (!isPaint) Div({
            ref { element ->
                editor = element
                if (element.innerHTML != html) element.innerHTML = html
                onDispose { if (editor == element) editor = null }
            }
            attr("contenteditable", "true")
            classes(NotepadStyles.editor, NotepadStyles.editorScrollable)
            style {
                property("flex", "1")
                minHeight(0.px)
                overflowY("auto")
                padding(18.px, 18.px, 78.px, 18.px)
                property("box-sizing", "border-box")
                color(Color.white)
                fontSize(18.px)
                property("line-height", "1.5")
                property("white-space", "pre-wrap")
                property("overflow-wrap", "break-word")
                outline("none")
                property("-webkit-overflow-scrolling", "touch")
            }
            onKeyUp {
                html = editor?.innerHTML.orEmpty()
                savedRange = captureEditorSelection(editor)
            }
            onKeyDown { event ->
                if (event.key == "Enter") {
                    val mode = activeInsertMode ?: return@onKeyDown
                    event.preventDefault()
                    restoreEditorSelection(editor, savedRange)
                    insertHtmlAtSelection(
                        editor,
                        when (mode) {
                            "STEP" -> "<br>${stepTokenHtml()}&nbsp;"
                            else -> "<br>${checkboxTokenHtml(false)}&nbsp;"
                        }
                    )
                    html = editor?.innerHTML.orEmpty()
                    savedRange = captureEditorSelection(editor)
                    onSave(currentNote())
                }
            }
            onMouseUp { savedRange = captureEditorSelection(editor) }
            onClick { event ->
                val target = event.nativeEvent.target as? HTMLElement
                if (target != null && toggleCheckboxToken(target)) {
                    html = editor?.innerHTML.orEmpty()
                    onSave(currentNote())
                }
                savedRange = captureEditorSelection(editor)
            }
            onBlur {
                savedRange = captureEditorSelection(editor)
                onSave(currentNote())
            }
        })
        Div({
            style {
                position(Position.Absolute)
                property("z-index", "30")
                bottom(12.px)
                left(50.percent)
                property("transform", "translateX(-50%)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                padding(6.px)
                borderRadius(22.px)
                property("background", "rgba(17,20,27,0.90)")
                property("box-shadow", "0 8px 24px rgba(0,0,0,.32)")
            }
        }) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) {
            if (isPaint) Div({
                attr("title", if (paintInteraction == "PAN") "Canvas verschieben aktiv" else "Canvas verschieben")
                style { width(34.px); height(34.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); cursor("pointer"); borderRadius(50.percent); backgroundColor(if (paintInteraction == "PAN") DashboardTheme.ACCENT else Color.transparent) }
                onClick { paintInteraction = if (paintInteraction == "DRAW") "PAN" else "DRAW" }
            }) { Img(src = "images/touch.svg", attrs = { style { width(22.px); height(22.px) } }) }
            if (!isPaint) Div({
                attr("title", "Strich / Step")
                style { width(34.px); height(34.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); cursor("pointer"); borderRadius(8.px); backgroundColor(if (activeInsertMode == "STEP") DashboardTheme.ACCENT else Color.transparent) }
                onMouseDown {
                    it.preventDefault()
                    savedRange = captureEditorSelection(editor)
                    activeInsertMode = if (activeInsertMode == "STEP") null else "STEP"
                    if (activeInsertMode == "STEP") insertToken(stepTokenHtml())
                }
            }) { Img(src = "images/step.svg", attrs = { style { width(18.px); height(18.px); property("filter", "brightness(0) invert(1)") } }) }
            Div {
                Div({
                    attr("title", "Farbe")
                    style { width(34.px); height(34.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); cursor("pointer") }
                    onClick { showColorPalette = !showColorPalette }
                }) { Img(src = "images/farbpalette.svg", attrs = { style { width(22.px); height(22.px) } }) }
            }
            if (!isPaint) Div({
                attr("title", "Checkbox")
                style { width(34.px); height(34.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); cursor("pointer"); borderRadius(8.px); backgroundColor(if (activeInsertMode == "CHECKBOX") DashboardTheme.ACCENT else Color.transparent) }
                onMouseDown {
                    it.preventDefault()
                    savedRange = captureEditorSelection(editor)
                    activeInsertMode = if (activeInsertMode == "CHECKBOX") null else "CHECKBOX"
                    if (activeInsertMode == "CHECKBOX") insertToken(checkboxTokenHtml(false))
                }
            }) { LumaticaCheckboxSymbol(checked = activeInsertMode == "CHECKBOX", fontSizePx = 18) }
            }
            if (showColorPalette) Div({
                style { padding(6.px, 2.px, 2.px); property("border-top", "1px solid rgba(255,255,255,.10)") }
            }) {
                NoteColorPicker(noteColor) { selected ->
                    noteColor = selected
                    showColorPalette = false
                    if (!isPaint) {
                        restoreEditorSelection(editor, savedRange)
                        editor?.focus()
                        document.asDynamic().execCommand("foreColor", false, selected)
                        html = editor?.innerHTML.orEmpty()
                        savedRange = captureEditorSelection(editor)
                        onSave(currentNote())
                    }
                }
            }
        }
        if (isPaint) {
        NoteDrawingOverlay(
            canvasId = "mobile-note-drawing-${note.id}",
            initialImageDataUrl = drawingData,
            initialColor = noteColor,
            showControls = false,
            interactionMode = paintInteraction,
            expandableCanvas = true,
            initialPanX = note.imageValues["paintPanX"]?.toDoubleOrNull() ?: 0.0,
            initialPanY = note.imageValues["paintPanY"]?.toDoubleOrNull() ?: 0.0,
            onColorChange = { noteColor = it },
            onCancel = {},
            onInsert = { dataUrl -> drawingData = dataUrl; onSave(currentNote()) }
        )
        }
    }
}
